import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import JornadaService from "../../services/JornadaService";
import { avisoConAccionSx } from "./temaJornada";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { DiaSemana, DiasHorario, IErrorTramo, IJornadaHorariosPersona, ITramo } from "../../types/Jornada";
import NoDisponibleLectura from "./NoDisponibleLectura";
import { DIAS_SEMANA, LABORABLES, NOMBRE_DIA, erroresTramos, fechaCorta, fechaLarga, horarioVigente, resumenHorario, totalSemanal } from "../../utils/Jornada";
import * as constantes from "../../common/Constantes";

// Editor del horario de una persona. Nunca se edita una versión que ya ha empezado: al guardar se crea otra desde la
// fecha de «Aplicar desde» (con la misma fecha que la última, la sustituye). Si hay un cambio programado, se edita ese.
interface HorarioDialogProps {
    open: boolean;
    especialistaId: number | null;
    nombre: string;
    onClose: () => void;
    onGuardado: () => void;
    setAlert: (alert: IAlert) => void;
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string; errores?: IErrorTramo[] } } };

const TRAMO_NUEVO: ITramo = { inicio: '09:00', fin: '14:00' };

// Solo los días con tramos, para guardar
const limpiarDias = (dias: DiasHorario): DiasHorario =>
    DIAS_SEMANA.reduce<DiasHorario>((acc, dia) => ((dias[dia]?.length ?? 0) > 0 ? { ...acc, [dia]: dias[dia] } : acc), {});

const HorarioDialog = ({ open, especialistaId, nombre, onClose, onGuardado, setAlert }: HorarioDialogProps) => {
    const history = useNavigate();
    // En el móvil, a pantalla completa: los días con sus tramos no caben en un diálogo
    const esMovil = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [datos, setDatos] = useState<IJornadaHorariosPersona | null>(null);
    const [dias, setDias] = useState<DiasHorario>({});
    const [desde, setDesde] = useState('');
    const [errores, setErrores] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [copiarDesde, setCopiarDesde] = useState<DiaSemana | null>(null);
    const [copiarA, setCopiarA] = useState<DiaSemana[]>([]);

    const tratarError = (e: unknown, metodo: string, porDefecto: string) => {
        const fallo = e as Fallo;
        if (fallo.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
            return;
        }
        if (![400, 403, 404].includes(fallo.response?.status ?? 0)) {
            const error: ILog = { method: `HorarioDialog.${metodo}`, level: 'error', message: fallo.message ?? '', meta: fallo.response };
            LogService.logError(error);
        }
        const deLaApi = fallo.response?.data?.errores;
        if (deLaApi?.length) setErrores(deLaApi.reduce<Record<string, string>>((acc, er) => ({ ...acc, [`${er.dia}-${er.indice}`]: er.mensaje }), {}));
        setAlert({ open: true, severity: 'error', msg: fallo.response?.data?.message || porDefecto });
    };

    // Abre en el cambio programado si lo hay; si no, en el horario vigente (o vacío), aplicando desde hoy
    const cargar = (respuesta: IJornadaHorariosPersona) => {
        setDatos(respuesta);
        const ultima = respuesta.versiones[respuesta.versiones.length - 1];
        const programada = ultima && ultima.desde > respuesta.hoy ? ultima : null;
        setDias(programada?.dias ?? horarioVigente(respuesta.versiones, respuesta.hoy)?.dias ?? {});
        setDesde(programada ? programada.desde : (respuesta.hoy > respuesta.minDesde ? respuesta.hoy : respuesta.minDesde));
        setErrores({});
    };

    useEffect(() => {
        if (!open || especialistaId === null) return;
        setLoading(true);
        setDatos(null);
        JornadaService.getHorariosPersona(especialistaId)
            .then((response: { data: IJornadaHorariosPersona }) => cargar(response.data))
            .catch((e: unknown) => { tratarError(e, 'getHorariosPersona', 'No se ha podido cargar el horario'); onClose(); })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, especialistaId]);

    const cambiarTramo = (dia: DiaSemana, indice: number, campo: keyof ITramo, valor: string) => {
        setDias(actual => ({ ...actual, [dia]: (actual[dia] ?? []).map((t, i) => (i === indice ? { ...t, [campo]: valor } : t)) }));
        setErrores({});
    };
    const anadirTramo = (dia: DiaSemana) => {
        setDias(actual => {
            const tramos = actual[dia] ?? [];
            // El segundo tramo empieza donde acaba el primero, para escribir menos
            const ultimo = tramos[tramos.length - 1];
            const nuevo = ultimo ? { inicio: ultimo.fin, fin: ultimo.fin } : TRAMO_NUEVO;
            return { ...actual, [dia]: [...tramos, nuevo] };
        });
        setErrores({});
    };
    const quitarTramo = (dia: DiaSemana, indice: number) => {
        setDias(actual => ({ ...actual, [dia]: (actual[dia] ?? []).filter((_t, i) => i !== indice) }));
        setErrores({});
    };

    const abrirCopiar = (dia: DiaSemana) => {
        setCopiarDesde(dia);
        setCopiarA(LABORABLES.filter(d => d !== dia));
    };
    const copiar = () => {
        if (!copiarDesde) return;
        const tramos = dias[copiarDesde] ?? [];
        setDias(actual => copiarA.reduce<DiasHorario>((acc, d) => ({ ...acc, [d]: tramos.map(t => ({ ...t })) }), { ...actual }));
        setCopiarDesde(null);
        setErrores({});
    };

    const guardar = () => {
        if (especialistaId === null || !datos) return;
        const encontrados = erroresTramos(dias);
        if (Object.keys(encontrados).length > 0) {
            setErrores(encontrados);
            setAlert({ open: true, severity: 'warning', msg: 'Revisa los tramos marcados' });
            return;
        }
        if (Object.keys(limpiarDias(dias)).length === 0) {
            setAlert({ open: true, severity: 'warning', msg: 'Indica al menos un día con su hora de inicio y de fin para guardar el horario' });
            return;
        }
        setGuardando(true);
        JornadaService.guardarHorario(especialistaId, desde, limpiarDias(dias))
            .then(() => {
                setAlert({ open: true, severity: 'success', msg: 'Horario guardado correctamente' });
                onGuardado();
                onClose();
            })
            .catch((e: unknown) => tratarError(e, 'guardarHorario', 'No se ha podido guardar el horario'))
            .finally(() => setGuardando(false));
    };

    const borrarProgramado = (id: string) => {
        if (especialistaId === null) return;
        setGuardando(true);
        JornadaService.borrarHorario(id)
            .then(() => JornadaService.getHorariosPersona(especialistaId))
            .then((response: { data: IJornadaHorariosPersona }) => {
                cargar(response.data);
                onGuardado();
                setAlert({ open: true, severity: 'success', msg: 'Cambio programado borrado' });
            })
            .catch((e: unknown) => tratarError(e, 'borrarHorario', 'No se ha podido borrar el cambio programado'))
            .finally(() => setGuardando(false));
    };

    const ultima = datos?.versiones[datos.versiones.length - 1];
    const programados = datos ? datos.versiones.filter(v => v.desde > datos.hoy) : [];
    const editandoProgramado = !!ultima && !!datos && ultima.desde > datos.hoy;
    const vigente = datos ? horarioVigente(datos.versiones, datos.hoy) : null;
    const fueraDeLimite = !!datos && desde !== '' && desde < datos.minDesde;

    return (
        <Dialog open={open} maxWidth="md" fullWidth fullScreen={esMovil} onClose={onClose}>
            <DialogTitle>Horario de {nombre}</DialogTitle>
            <DialogContent>
                {loading || !datos ? <CircularProgress /> : (
                    <>
                        {editandoProgramado && ultima && (
                            <Alert severity="info" sx={{ mb: 2, ...avisoConAccionSx }}
                                action={<Button color="inherit" size="small" disabled={guardando} onClick={() => borrarProgramado(ultima._id)}>Borrar cambio programado</Button>}>
                                Este horario se aplica desde el {fechaLarga(ultima.desde)}. Hasta entonces sigue el actual: {resumenHorario(vigente?.dias)}.
                            </Alert>
                        )}
                        {programados.filter(v => v._id !== ultima?._id).map(v => (
                            <Alert key={v._id} severity="info" sx={{ mb: 1, ...avisoConAccionSx }}
                                action={<Button color="inherit" size="small" disabled={guardando} onClick={() => borrarProgramado(v._id)}>Borrar</Button>}>
                                Otro cambio programado desde el {fechaLarga(v.desde)}: {resumenHorario(v.dias)}.
                            </Alert>
                        ))}

                        {DIAS_SEMANA.map(dia => {
                            const tramos = dias[dia] ?? [];
                            return (
                                <Box key={dia} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, py: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                    {/* En el móvil, el nombre del día encima de sus tramos */}
                                    <Typography sx={{ width: { xs: '100%', sm: 90 }, pt: { xs: 0, sm: 1 }, fontWeight: { xs: 500, sm: 400 } }}>{NOMBRE_DIA[dia]}</Typography>
                                    <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}>
                                        {tramos.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>No trabaja</Typography>}
                                        {tramos.map((tramo, i) => {
                                            const error = errores[`${dia}-${i}`];
                                            return (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                    <TextField type="time" size="small" label="Inicio" value={tramo.inicio} error={!!error} inputProps={{ step: 300 }} sx={{ flex: { xs: 1, sm: 'none' } }} onChange={(e) => cambiarTramo(dia, i, 'inicio', e.target.value)} />
                                                    <TextField type="time" size="small" label="Fin" value={tramo.fin} error={!!error} helperText={error} inputProps={{ step: 300 }} sx={{ flex: { xs: 1, sm: 'none' } }} onChange={(e) => cambiarTramo(dia, i, 'fin', e.target.value)} />
                                                    <Tooltip title="QUITAR TRAMO"><IconButton size="small" onClick={() => quitarTramo(dia, i)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button size="small" startIcon={<AddIcon />} onClick={() => anadirTramo(dia)}>{tramos.length === 0 ? 'Añadir tramo' : 'Otro tramo'}</Button>
                                        {tramos.length > 0 && <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => abrirCopiar(dia)}>Copiar a…</Button>}
                                    </Box>
                                </Box>
                            );
                        })}

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Total: {totalSemanal(dias)}</Typography>

                        <Divider sx={{ my: 2 }} />
                        <TextField type="date" size="small" label="Aplicar desde" value={desde} InputLabelProps={{ shrink: true }}
                            inputProps={{ min: datos.minDesde }}
                            error={fueraDeLimite}
                            helperText={fueraDeLimite ? `Elige el ${fechaCorta(datos.minDesde)} o una fecha posterior` : undefined}
                            onChange={(e) => setDesde(e.target.value)} />
                        {ultima && desde === ultima.desde && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Sustituye al horario que empieza ese mismo día.</Typography>
                        )}
                        {desde !== '' && desde < datos.inicioMes && (
                            <Alert severity="warning" sx={{ mt: 1 }}>Se recalculará el previsto desde el {fechaLarga(desde)}. Los fichajes no cambian.</Alert>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2">No disponibles</Typography>
                        {datos.noDisponibles.length === 0
                            ? <Typography variant="body2" color="text.secondary">Ninguno en curso ni próximo.</Typography>
                            : datos.noDisponibles.map(n => <NoDisponibleLectura key={n._id} noDisponible={n} />)}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Se gestionan en Configuración › Usuarios.</Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {guardando ? <CircularProgress size={24} sx={{ mr: 2 }} /> : (
                    <>
                        <Button onClick={guardar} disabled={loading || !datos || desde === '' || fueraDeLimite}>Guardar</Button>
                        <Button onClick={onClose}>Cancelar</Button>
                    </>
                )}
            </DialogActions>

            {/* COPIAR UN DÍA A OTROS */}
            <Dialog open={copiarDesde !== null} onClose={() => setCopiarDesde(null)}>
                <DialogTitle>Copiar el {copiarDesde ? NOMBRE_DIA[copiarDesde].toLowerCase() : ''} a…</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {DIAS_SEMANA.filter(d => d !== copiarDesde).map(d => (
                            <FormControlLabel key={d} label={NOMBRE_DIA[d]}
                                control={<Checkbox checked={copiarA.includes(d)} onChange={(e) => setCopiarA(actual => e.target.checked ? [...actual, d] : actual.filter(x => x !== d))} />} />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={copiar} disabled={copiarA.length === 0}>Copiar</Button>
                    <Button onClick={() => setCopiarDesde(null)}>Cancelar</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default HorarioDialog;
